/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, { useState, useEffect } from 'react';
import { useDispatch, connect } from 'react-redux';
import Paper from '@material-ui/core/Paper';
import withStyles from '@material-ui/core/styles/withStyles';
import {
    object, shape, arrayOf, string, bool,
} from 'prop-types';
import Grid from '@material-ui/core/Grid';
import Radio from '@material-ui/core/Radio';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import { Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import IconButton from '@material-ui/core/IconButton';
import parseMarkdown from '../../../../helpers/contentstack/parseMarkdown';
import SubscriptionModalPopup from './SubscriptionModalPopup';
import { trackEvent } from '../../../../../state/ducks/TagManager/ducks/TagManager/TagManager-Actions';
import { getUserConfirmationForSubscriptionPopup } from '../../../../../state/ducks/App/App-Selectors';

const styles = (theme) => ({
    container: {
        marginBottom: '20px',
        color: theme?.palette?.common?.black,
        [theme.breakpoints.up('600')]: {
            border: `1px solid ${theme.palette?.pdp?.cardBorder}`,
            marginBottom: '15px',
        },
        boxShadow: 'none',
        borderRadius: '3px',
    },
    paperHeader: {
        boxSizing: 'border-box',
        width: '100%',
        padding: '12px 15px',
        fontWeight: '600',
        fontSize: '15px',
        lineHeight: '19px',
        color: theme.palette.colorPrimary,
        background: theme.palette.common?.lightPurple,
        borderRadius: '4px 4px 0 0',
        textTransform: 'uppercase',
        opacity: 0.9,
        [theme.breakpoints.down('sm')]: {
            padding: '12px 20px',
        },
    },
    paperBody: {
        padding: '20px 20px 20px 12px',
        [theme.breakpoints.down('sm')]: {
            padding: '20px 20px 10px 20px',
        },
        '& label': {
            marginRight: '0px !important',
            '& span': {
                '&$radio': {
                    '&> span': {
                        position: 'relative',
                        display: 'block !important',
                        width: '26px',
                        height: '26px',
                        borderRadius: '50%',
                        background: `${theme.palette.common?.white} !important`,
                        border: `1px solid ${theme.palette.common?.black}`,
                        '& $root': {
                            display: 'none',
                        },
                        '&:last-child': {
                            display: 'none !important',
                        },
                        '& div': {
                            display: 'none',
                        },
                    },
                },
            },
        },
    },
    copyContainer: {
        color: theme.palette.common.red || '#d90059',
        '& ul': {
            margin: '0 0 0 17px',
            lineHeight: 1.8,
            fontSize: '14px',
        },
    },
    formLabel: {
        display: 'flex',
        width: '100%',
    },
    radio: {
        color: theme.palette.colorPrimary,
        padding: '6px 11px',
        position: 'relative',
        display: 'block !important',
        '&$checked': {
            color: theme.palette.colorPrimary,
        },
        [theme.breakpoints.down('600')]: {
            '& svg': {
                fontSize: '24px',
            },
        },
    },
    checked: {
        color: theme.palette.colorPrimary,
        '&$checked': {
            color: theme.palette.colorPrimary,
        },
        '& span:before': {
            cursor: 'pointer',
            display: 'block',
            content: '"" !important',
            borderRadius: '100%',
            height: '16px',
            width: '16px',
            transition: 'background',
            background: '#744C96',
            position: 'absolute',
            left: '50%',
            top: '50%',
            transform: 'translate(-50%, -50%)',
        },
    },
    learnMore: {
        padding: '0px',
        fontSize: '1.1em',
        textDecoration: 'underline',
        color: theme.palette.black || '#000',
        '&:hover': {
            backgroundColor: 'inherit',
        },
    },
    subscriptionLabel: {
        width: 'auto',
        marginRight: '6px',
        '& span': {
            fontSize: '16px',
        },
    },
    subscriptionButton: {
        display: 'flex',
    },
});

const SubscriptionProductBannerUI = ({
    classes, location, content, isUserConfirmed,
}) => {
    const entries = content?.entries?.[0] || {};
    const subscription = entries?.subscriptions || {};
    const termModal = subscription?.terms_modal || '';
    const subscriptionUrl = location?.pathname.includes('subscription');
    const modalOpen = (subscriptionUrl && !isUserConfirmed);
    const [isShowing, setIsShowing] = useState(modalOpen);
    function toggle() {
        setIsShowing(!isShowing);
    }
    const standardProduct = entries?.url || '';
    const subscriptionProduct = subscription?.product_url  || '';
    const standardProductUrl = subscriptionUrl ? subscriptionProduct : standardProduct;
    const subscriptionProductUrl = subscriptionUrl ? standardProduct : subscriptionProduct;
    const subscriptionCopy = subscription?.offer_copy || '';

    const dispatch = useDispatch();

    useEffect(() => {
        // Track purchase option selection
        if (!location.pathname.includes('subscription')) {
            const purchaseOptionEventObj = {
                eventCategory: 'Product Page',
                eventAction: 'Purchase Option',
                eventLabel: 'Impression',
            };
            dispatch(trackEvent(purchaseOptionEventObj));
        }
    }, []);

    const shouldRenderLink = (url) => (
        // purpose: to avoid unnecessary page refresh, determine whether or not link should be rendered on radio button.
        // if current url matches the button's url, only radio button will be rendered.
        // otherwise, a link to the other url will be added to the button.
        // param: url is either standard product url or subscription product url.
        location.pathname === url ? (
            <Radio
                value={url}
                classes={{
                    root: classes.radio,
                    checked: classes.checked,
                }}
                checked
            />
        ) : (
            <Link
                to={url}
            >
                <Radio
                    value={url}
                    classes={{
                        root: classes.radio,
                        checked: classes.checked,
                    }}
                    checked={false}
                />
            </Link>
        )
    );

    return (
        <Paper className={classes.container}>
            <div className={classes.paperHeader}>Purchasing Options</div>
            <div className={classes.paperBody}>
                <Grid
                    container
                    direction="column"
                    justify="flex-start"
                >
                    <FormControlLabel
                        id="radio-button-sub"
                        label="One time purchase"
                        className={classes.formLabel}
                        control={shouldRenderLink(standardProductUrl)}
                    />
                    <div className="subscriptionButton">
                        <FormControlLabel
                            id="radio-button-sub"
                            label="Subscribe and save"
                            className={classes.subscriptionLabel}
                            control={shouldRenderLink(subscriptionProductUrl)}
                        />
                        <IconButton className={classes.learnMore} onClick={toggle}>
                            <span>Learn More</span>
                        </IconButton>
                    </div>
                    <div className={classes.copyContainer}><ReactMarkdown source={parseMarkdown(subscriptionCopy)} escapeHtml={false} /></div>
                    <SubscriptionModalPopup
                        isShowing={isShowing}
                        closeModal={toggle}
                        subscriptionUrl={subscriptionProductUrl}
                        location={location}
                        termModal={termModal}
                    />
                </Grid>
            </div>
        </Paper>
    );
};

SubscriptionProductBannerUI.propTypes = {
    classes: object.isRequired,
    location: shape({
        pathname: string.isRequired,
    }).isRequired,
    content: shape({
        entries: arrayOf(shape({
            subscriptions: shape({
                product_url: string.isRequired,
            }).isRequired,
            url: string.isRequired,
        }).isRequired),
    }).isRequired,
    isUserConfirmed: bool.isRequired,
};

const mapStateToProps = (state) => ({
    isUserConfirmed: getUserConfirmationForSubscriptionPopup(state),
});

const styledSubscriptionProductBannerUI = withStyles(styles)(SubscriptionProductBannerUI);
export default connect(mapStateToProps)(styledSubscriptionProductBannerUI);
